import { defineStore } from 'pinia'
import type { Workspace, Feature, Conversation } from '~/types'

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    workspaces: [] as Workspace[],
    currentWorkspace: null as Workspace | null,
    features: {} as Feature
  }),

  actions: {
    async fetchWorkspaces() {
      const { $api } = useNuxtApp()
      await $api.get("/workspaces").then(async (response) => {
        const data = response.data;

        // check if exist less one workspace
        if (data.length === 0) {
          return navigateTo('/workspaces/create');
        }

        // set workspaces
        data.forEach((workspace: Workspace) => {
          this.setWorkspace(workspace);
        });

        // set current workspace
        const userStore = useUserStore();
        const currentWorkspace = data.find((workspace: Workspace) => workspace.id === userStore.user?.current_workspace_id);
        this.setCurrentWorkspace(currentWorkspace);

        // get user notification preferences
        await userStore.fetchNotificationPreferences();
      });
    },

    setWorkspace(workspace: Workspace) {
      // check if workspace it's already added
      const existingWorkspace = this.workspaces.find(w => w.id === workspace.id)
      if (!existingWorkspace) {
        this.workspaces.push(workspace)
      }
    },

    setCurrentWorkspace(workspace: Workspace) {
      this.currentWorkspace = workspace

      // remove old workspace listener
      this.removeWorkspaceListener();

      // start workspace listener
      this.startWorkspaceListener();
    },

    async fetchFeatures() {
      const { $api } = useNuxtApp()
      await $api.get('/features')
      .then((response) => {
        this.features = response.data
      })
    },

    async startWorkspaceListener() {
      const { $pusher } = useNuxtApp()
      const conversationStore = useConversationStore();

      // workspace
      const channel = $pusher.subscribe(`workspaces.${this.currentWorkspace?.id}`);

      // onboarding task completed
      channel.bind('onboarding.task-completed', (data: { task: string }) => {
        // @ts-ignore
        this.currentWorkspace.onboarding_tasks.tasks[data.task] = true;
      });

      // Listen for new conversations
      channel.bind('conversation.new', (e: any) => {
        conversationStore.receiveNewConversation(e.conversation);
      });

      // Listen for update the last message of a conversation
      channel.bind('conversation.update-last-message', (e: any) => {
        const conversation = conversationStore.conversations?.data.find((c: Conversation) => c.id === e.message.conversation_id);
        if (conversation) {
          conversation.last_conversation_message = e.message;
        }
      });
    },

    async removeWorkspaceListener() {
      const { $pusher } = useNuxtApp()
      $pusher.unsubscribe(`workspaces.${this.currentWorkspace?.id}`);
    },

    clear() {
      this.workspaces = []
      this.currentWorkspace = null
    }
  }
})